import { capacidades } from '@/platform';
import { httpClient } from '@/shared/api/httpClient';
import { lerOrigem, viaDoAlvo, type OrigemCliente, type ViaCadastro } from '@/shared/origem/lerOrigem';
import {
  sanitizarComoConheceu,
  sanitizarComoConheceuDetalhe,
  type ComoConheceu,
} from '@/shared/origem/comoConheceu';

export type ResumoUsuario = {
  id: string;
  email: string;
  nomeEmpresa: string;
  plano: string;
  status: string;
  maxMaquinas: number;
  criadoEm: string;
};

export type ResumoMaquina = {
  id: string;
  machineId: string;
  nome: string;
  ativa: boolean;
  ultimoLoginEm: string | null;
  criadoEm: string;
};

export type RespostaLogin = {
  token: string;
  expiraEm: string;
  usuario: ResumoUsuario;
  maquina: ResumoMaquina;
};

/**
 * Os quatro primeiros descrevem a tolerância offline do desktop, que tem
 * banco local. `sem-servidor` é exclusivo do app web: lá não há dado
 * local, então perder o servidor não é "trabalhar offline" — é não
 * trabalhar. O servidor nunca devolve esse valor; quem o atribui é o
 * `useAuth` ao ver o backend inalcançável.
 */
export type StatusConexao =
  | 'online'
  | 'offline-ok'
  | 'offline-leitura'
  | 'offline-bloqueado'
  | 'sem-servidor';

export type EstadoAuth = {
  temConta: boolean;
  logado: boolean;
  status: StatusConexao;
  diasDesdeOnline: number | null;
  diasParaModoLeitura: number | null;
  diasParaBloqueio: number | null;
  conta: {
    usuarioId: string;
    email: string;
    nomeEmpresa: string;
    plano: string;
    status: string;
    maxMaquinas: number;
    maquinaId: string;
    ultimoLoginOnlineEm: string;
    ultimoContatoOnlineEm: string;
    tokenExpiraEm: string;
  } | null;
};

export type Perfil = {
  usuario: ResumoUsuario;
  maquinas: ResumoMaquina[];
};

export type RespostaSignup = {
  usuario: ResumoUsuario;
  precisaVerificar: boolean;
};

/**
 * Corpo do `POST /api/auth/signup`. `origem` é o primeiro toque gravado pela
 * landing (ausente no desktop e em quem chegou direto); `via` diz em qual
 * alvo o cadastro aconteceu; `comoConheceu`/`comoConheceuDetalhe` são o que
 * a pessoa declarou no formulário. Todos opcionais para o servidor.
 */
export type CorpoSignup = {
  email: string;
  senha: string;
  nomeEmpresa: string;
  origem?: OrigemCliente;
  via: ViaCadastro;
  comoConheceu?: ComoConheceu;
  comoConheceuDetalhe?: string;
};

export type DadosSignup = {
  email: string;
  senha: string;
  nomeEmpresa: string;
  comoConheceu?: ComoConheceu;
  comoConheceuDetalhe?: string;
};

export const authApi = {
  estado: () => httpClient.get<EstadoAuth>('/api/auth/estado'),
  signup: (dados: DadosSignup) => {
    const body: CorpoSignup = {
      email: dados.email,
      senha: dados.senha,
      nomeEmpresa: dados.nomeEmpresa,
      origem: lerOrigem(),
      via: viaDoAlvo(capacidades.alvo),
      comoConheceu: sanitizarComoConheceu(dados.comoConheceu),
      comoConheceuDetalhe: sanitizarComoConheceuDetalhe(dados.comoConheceuDetalhe),
    };
    return httpClient.post<RespostaSignup>('/api/auth/signup', body);
  },
  verificar: (body: {
    email: string;
    codigo: string;
    senha: string;
    nomeMaquina?: string;
  }) => httpClient.post<RespostaLogin>('/api/auth/verificar', body),
  reenviarVerificacao: (body: { email: string }) =>
    httpClient.post<{ ok: true }>('/api/auth/reenviar-verificacao', body),
  login: (body: { email: string; senha: string; nomeMaquina?: string }) =>
    httpClient.post<RespostaLogin>('/api/auth/login', body),
  loginOffline: (body: { email?: string; senha: string }) =>
    httpClient.post<{ ok: true }>('/api/auth/login-offline', body),
  contasCacheadas: () =>
    httpClient.get<{ contas: Array<{ usuarioId: string; email: string }> }>(
      '/api/auth/contas-cacheadas',
    ),
  logout: () => httpClient.post<void>('/api/auth/logout', {}),
  recover: (body: { email: string }) =>
    httpClient.post<{ ok: true; mensagem: string }>('/api/auth/recover', body),
  me: () => httpClient.get<Perfil>('/api/auth/me'),
  revalidar: () => httpClient.post<EstadoAuth>('/api/auth/revalidar', {}),
  ping: () =>
    httpClient.post<{ ok: boolean; estado: EstadoAuth }>('/api/auth/ping', {}),
  sincronizarAgora: () =>
    httpClient.post<{
      servidorOnline: boolean;
      eventosEnviados: number;
      orcamentosNumerados: number;
      avisos: string[];
      estado: EstadoAuth;
    }>('/api/sync/agora', {}),
  removerMaquina: (maquinaId: string) =>
    httpClient.delete<void>(`/api/auth/maquinas/${maquinaId}`),
};
